'use client';

import { useTranslations } from 'next-intl';
import { unwrapResultSuccess } from '@headless-workspace/core-domain';
import {
    AllStoresStockStatus,
    ProductDeliveryAllStoresValue,
    ProductDeliveryStoreValue,
    ProductDeliveryWarehouseValue,
    StoreStockStatus,
    UserInfo,
    WarehouseStockStatus,
} from '../../../../server';
import { useProductDeliveryInfo } from '../../delivery';
import { freeShippingBusinessRules } from '../../shipping';
import { ProductType } from '../../types';
import {
    ChipStatus,
    mapAllStoresStockStatusToChipStatus,
    mapStoreStockStatusToChipStatus,
    mapWarehouseStockStatusToChipStatus,
} from '../ChipStatus';
import { useProductDetailContext } from '../productDetailProvider';

type UseProductDeliveryBusinessRulesReturnType =
    | {
          isLoading: true;
      }
    | {
          isLoading: false;
          warehouse: DeliveryInformation;
          store: DeliveryInformation;
      };

export enum CtaType {
    SelectStore = 'SelectStore',
    ChangeStore = 'ChangeStore',
    NotifyMe = 'NotifyMe',
}

export type DeliveryInformationStatus = {
    chip: ChipStatus;
    label: string;
};

type DeliveryInformation = {
    title: string;
    description?: string;
    status: DeliveryInformationStatus;
    cta?: {
        label: string;
        type: CtaType;
    };
    storeId?: string;
};

type UseProductDeliveryBusinessRulesArgs = {
    userInfo?: UserInfo;
};

export const useProductDeliveryBusinessRules = ({
    userInfo,
}: UseProductDeliveryBusinessRulesArgs): UseProductDeliveryBusinessRulesReturnType => {
    const { productDetails } = useProductDetailContext();

    const { isLoading, result: productDeliveryInfoResult } = useProductDeliveryInfo(
        productDetails.type !== ProductType.PRODUCT_SET ? productDetails.sku : undefined,
    );
    const productDeliveryInfo = unwrapResultSuccess(productDeliveryInfoResult);

    const warehouse = useWarehouseDeliveryBusinessRules(
        productDeliveryInfo?.warehouse,
        productDeliveryInfo?.maxFreeShippingPrice,
        productDetails.price,
        userInfo,
    );

    const store = useStoreDeliveryBusinessRules(productDeliveryInfo?.currentStore, productDeliveryInfo?.allStores);

    if (isLoading) {
        return { isLoading };
    }

    return { isLoading, warehouse, store };
};

const useWarehouseDeliveryBusinessRules = (
    warehouse?: ProductDeliveryWarehouseValue,
    maxFreeShippingPrice?: number,
    price?: number,
    userInfo?: UserInfo,
): DeliveryInformation => {
    const t = useTranslations('Discover.Pdp.deliveryZone');

    const isFreeShipping = freeShippingBusinessRules({
        price,
        threshold: maxFreeShippingPrice,
        userInfo,
    });

    const title = isFreeShipping ? t('label.offeredShippingOrRelayPoint') : t('label.shippingOrRelayPoint');

    if (!warehouse) {
        return {
            title,
            status: {
                chip: mapWarehouseStockStatusToChipStatus(WarehouseStockStatus.Unavailable),
                label: t('label.unavailable'),
            },
        };
    }

    const statusLabel: Record<WarehouseStockStatus, string> = {
        [WarehouseStockStatus.Available]: t('label.available'),
        [WarehouseStockStatus.Unavailable]: t('label.unavailable'),
        [WarehouseStockStatus.OutOfStock]: t('label.outOfStock'),
        [WarehouseStockStatus.NotifyMe]: t('label.outOfStock'),
    };

    return {
        title,
        description:
            warehouse.stockStatus === WarehouseStockStatus.Available ? warehouse.deliveryDescription : undefined,
        status: {
            chip: mapWarehouseStockStatusToChipStatus(warehouse.stockStatus),
            label: statusLabel[warehouse.stockStatus],
        },
        cta:
            warehouse.stockStatus === WarehouseStockStatus.NotifyMe
                ? {
                      label: t('action.notifyMe'),
                      type: CtaType.NotifyMe,
                  }
                : undefined,
    };
};

const useStoreDeliveryBusinessRules = (
    currentStore?: ProductDeliveryStoreValue,
    allStores?: ProductDeliveryAllStoresValue,
): DeliveryInformation => {
    const t = useTranslations('Discover.Pdp.deliveryZone');

    if (currentStore) {
        const clickAndCollectTitle = currentStore.clickAndCollectTitle || t('label.inStorePickup');

        const statusLabel: Record<StoreStockStatus, string> = {
            [StoreStockStatus.Available]: t('label.availableInSelectedStore', { storeName: currentStore.storeName }),
            [StoreStockStatus.StockLow]: t('label.availableLowStockInSelectedStore', {
                stock: currentStore.stock,
                storeName: currentStore.storeName,
            }),
            [StoreStockStatus.Unavailable]: t('label.unavailableInSelectedStore', {
                storeName: currentStore.storeName,
            }),
            [StoreStockStatus.UnavailableAllStores]: t('label.unavailable'),
            [StoreStockStatus.OutOfStock]: t('label.outOfStock'),
            [StoreStockStatus.AvailableDTS]: t('label.availableInSelectedStore', { storeName: currentStore.storeName }),
            [StoreStockStatus.UnavailableDTS]: t('label.unavailableInSelectedStore', {
                storeName: currentStore.storeName,
            }),
        };

        const getCTA = (): DeliveryInformation['cta'] => {
            if (
                currentStore.stockStatus === StoreStockStatus.OutOfStock ||
                currentStore.stockStatus === StoreStockStatus.UnavailableAllStores
            ) {
                return undefined;
            }

            return {
                label: t('action.changeStore'),
                type: CtaType.ChangeStore,
            };
        };

        const isDTS =
            currentStore.stockStatus === StoreStockStatus.AvailableDTS ||
            currentStore.stockStatus === StoreStockStatus.UnavailableDTS;
        return {
            title: isDTS ? t('label.inStorePickupDTS') : clickAndCollectTitle,
            status: {
                chip: mapStoreStockStatusToChipStatus(currentStore.stockStatus),
                label: statusLabel[currentStore.stockStatus],
            },
            cta: getCTA(),
            storeId: currentStore.storeId,
        };
    }

    if (allStores) {
        const clickAndCollectTitle = allStores.clickAndCollectTitle || t('label.inStorePickup');

        const statusLabel: Record<AllStoresStockStatus, string> = {
            [AllStoresStockStatus.Available]: t('label.available'),
            [AllStoresStockStatus.Unavailable]: t('label.unavailable'),
            [AllStoresStockStatus.OutOfStock]: t('label.unavailable'),
        };

        return {
            title: clickAndCollectTitle,
            description:
                allStores.stockStatus === AllStoresStockStatus.Available
                    ? t('label.selectStoreToDiscoverStock')
                    : undefined,
            status: {
                chip: mapAllStoresStockStatusToChipStatus(allStores.stockStatus),
                label: statusLabel[allStores.stockStatus],
            },
            cta:
                allStores.stockStatus === AllStoresStockStatus.Available
                    ? {
                          label: t('action.selectStore'),
                          type: CtaType.SelectStore,
                      }
                    : undefined,
        };
    }

    return {
        title: t('label.inStorePickup'),
        status: {
            chip: mapAllStoresStockStatusToChipStatus(AllStoresStockStatus.Unavailable),
            label: t('label.unavailable'),
        },
    };
};
